(() => {
  // Reuse an existing manager when this asset is included by multiple sections.
  if (window.OAParallaxManager) {
    window.OAParallaxManager.refresh();
    return;
  }

  // Declarative API read from section data attributes.
  const SECTION_SELECTOR = '[data-oa-parallax]';
  const DEFAULTS = {
    cssVar: '--oa-parallax-y',
    strength: 0.12,
    maxShift: 56,
    mobileBreakpoint: 767,
    rootMargin: '200px 0px'
  };

  // Active section instances keyed by section root element.
  const instances = new Map();
  // IntersectionObserver pools keyed by rootMargin to avoid one observer per section.
  const observerPools = new Map();
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let rafId = 0;

  const toNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const addMediaQueryListener = (mediaQuery, handler) => {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }

    return () => {};
  };

  const setOffset = (instance, value) => {
    instance.target.style.setProperty(instance.cssVar, `${value.toFixed(2)}px`);
  };

  // Share observers for sections with the same rootMargin.
  const getObserverPool = (rootMargin) => {
    if (observerPools.has(rootMargin)) return observerPools.get(rootMargin);

    const anchors = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const relatedSections = anchors.get(entry.target);
          if (!relatedSections) continue;

          for (const section of relatedSections) {
            const instance = instances.get(section);
            if (instance) instance.inView = entry.isIntersecting;
          }
        }
        scheduleUpdate();
      },
      { root: null, threshold: 0, rootMargin }
    );

    const pool = { observer, anchors };
    observerPools.set(rootMargin, pool);
    return pool;
  };

  const attachObserver = (instance) => {
    const pool = getObserverPool(instance.rootMargin);
    let relatedSections = pool.anchors.get(instance.anchor);

    if (!relatedSections) {
      relatedSections = new Set();
      pool.anchors.set(instance.anchor, relatedSections);
      pool.observer.observe(instance.anchor);
    }

    relatedSections.add(instance.section);

    // Instance-specific cleanup for observer bookkeeping.
    instance.detachObserver = () => {
      const currentPool = observerPools.get(instance.rootMargin);
      if (!currentPool) return;

      const currentSet = currentPool.anchors.get(instance.anchor);
      if (!currentSet) return;

      currentSet.delete(instance.section);
      if (currentSet.size === 0) {
        currentPool.anchors.delete(instance.anchor);
        currentPool.observer.unobserve(instance.anchor);
      }

      if (currentPool.anchors.size === 0) {
        currentPool.observer.disconnect();
        observerPools.delete(instance.rootMargin);
      }
    };
  };

  const calculateOffset = (instance, viewportCenter) => {
    const rect = instance.anchor.getBoundingClientRect();
    const sectionCenter = rect.top + (rect.height * 0.5);
    const raw = (viewportCenter - sectionCenter) * instance.strength;
    return clamp(raw, -instance.maxShift, instance.maxShift);
  };

  const updateAll = () => {
    rafId = 0;
    if (!instances.size) return;

    const viewportCenter = window.innerHeight * 0.5;
    const reducedMotion = reduceMotionQuery.matches;

    for (const instance of instances.values()) {
      if (!instance.inView || reducedMotion || instance.mobileQuery.matches) {
        setOffset(instance, 0);
        continue;
      }

      setOffset(instance, calculateOffset(instance, viewportCenter));
    }
  };

  // Batch parallax updates into a single animation frame.
  function scheduleUpdate() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(updateAll);
  }

  // Build one parallax instance from a section root and its data attributes.
  const initSection = (section) => {
    if (!(section instanceof HTMLElement)) return;
    if (instances.has(section) || section.dataset.oaParallaxInit === 'true') return;

    const targetSelector = section.dataset.oaParallaxTarget;
    const anchorSelector = section.dataset.oaParallaxAnchor;
    const target = targetSelector ? section.querySelector(targetSelector) : section;
    const anchor = anchorSelector ? section.querySelector(anchorSelector) : section;
    if (!target || !anchor) return;

    const mobileBreakpoint = toNumber(section.dataset.oaParallaxMobileBreakpoint, DEFAULTS.mobileBreakpoint);
    const mobileQuery = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);

    const instance = {
      section,
      target,
      anchor,
      cssVar: section.dataset.oaParallaxVar || DEFAULTS.cssVar,
      strength: toNumber(section.dataset.oaParallaxStrength, DEFAULTS.strength),
      maxShift: toNumber(section.dataset.oaParallaxMaxShift, DEFAULTS.maxShift),
      rootMargin: section.dataset.oaParallaxRootMargin || DEFAULTS.rootMargin,
      mobileQuery,
      inView: false,
      detachObserver: () => {},
      detachMediaListener: () => {}
    };

    instance.detachMediaListener = addMediaQueryListener(mobileQuery, scheduleUpdate);
    attachObserver(instance);
    instances.set(section, instance);
    section.dataset.oaParallaxInit = 'true';
  };

  const destroySection = (section) => {
    const instance = instances.get(section);
    if (!instance) return;

    instance.detachObserver();
    instance.detachMediaListener();
    setOffset(instance, 0);
    section.dataset.oaParallaxInit = '';
    instances.delete(section);
  };

  // Remove any managed sections that belong to a container being unloaded.
  const destroyInContainer = (container) => {
    if (!(container instanceof Element)) return;

    for (const section of instances.keys()) {
      if (container.contains(section)) destroySection(section);
    }
  };

  // Scan scope for new parallax-enabled sections.
  const refresh = (root = document) => {
    const scope = root instanceof Element || root instanceof Document ? root : document;

    if (scope instanceof Element && scope.matches(SECTION_SELECTOR)) {
      initSection(scope);
    }
    scope.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    scheduleUpdate();
  };

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);

  const detachReducedMotion = addMediaQueryListener(reduceMotionQuery, scheduleUpdate);
  void detachReducedMotion;

  // Keep runtime in sync with Shopify Theme Editor section lifecycle.
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyInContainer(event.target));
  document.addEventListener('shopify:section:reorder', scheduleUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  } else {
    refresh();
  }

  window.OAParallaxManager = {
    refresh,
    destroyInContainer
  };
})();
